/**
 * СНИМОК НА ДАТУ — орган, который отвечает на два вопроса: «как поле выглядело в этот день»
 * и «в какие дни его вообще видно».
 *
 * ПЯТЫЙ КРУГ: ЛЕЧИЛИ НЕПОНЯТНОСТЬ СЛОВАМИ, А НАДО БЫЛО ЗНАКАМИ. Прошлый заход убрал
 * прочерк и несимметричный тумблер — и поставил на их место «Показать календарь», «Показать
 * снимок», «Скрыть снимок» и строку отметки «не смотрели». Владелец: «сделай тогда иконку
 * календаря… что за подпись „мы не смотрели“? Не нужна. Смотрели мы или нет — разницы
 * никакой». Он прав дважды:
 *
 *   1. ВОКРУГ ЭТОГО ОРГАНА ВСЁ — ИКОНКИ. Инструменты контура в соседнем углу это 16 px
 *      штрих на сетке 24 (`DrawTools.tsx`, `glyph`). Текстовая кнопка среди них читается
 *      мусором, а не подписью. Календарь и глаз взяты ИЗ ТОГО ЖЕ СЛОВАРЯ, а не нарисованы
 *      заново: второй набор путей — это и есть механизм, которым одинаковые вещи начинают
 *      выглядеть по-разному в двух углах одной карты. Слова не потеряны: они в `aria-label`
 *      и `title`, ровно там же, где глаголы всех остальных иконок над картой.
 *   2. ПОДПИСЬ ПРО НАШУ РАБОТУ — НЕ ПОКАЗАНИЕ. «участвовала в расчёте», «не смотрели»,
 *      «дата не выбрана» — это протокол пайплайна. Он не меняет ни одного действия
 *      человека: снимок за эту дату он посмотрит всё равно. Строка отметки удалена целиком.
 *      Уцелело одно — ОТКЛОНЕНИЕ И ЕГО ПРИЧИНА, и то за «i»: оно объясняет, почему картинка
 *      хуже соседних и почему на ней не стоит ответ.
 *
 * ЧТО ЧЕМ УПРАВЛЯЕТ, сверху вниз: ряд «‹ дата ›» шагает по датам; ряд из двух иконок под
 * ним открывает календарь и показывает/прячет снимок; «i» появляется у отклонённой даты.
 * Ряд иконок стоит в разметке ВСЕГДА, когда есть чем управлять, — и в пустом состоянии,
 * где дат ещё не спрашивали, он и есть весь орган.
 *
 * ЧТО СОХРАНЕНО ИЗ ПРЕЖНИХ КРУГОВ (и трогать это нельзя):
 *   * ПАНЕЛЬ НЕПРОЗРАЧНА. Ни `backdrop-filter`, ни `opacity` — правило для ВСЕХ панелей в
 *     углах карты (визуальный контракт, правка 2026-08-05(д)).
 *   * ПРОЧЕРКА НЕ БЫВАЕТ. Без дат на их месте либо иконка календаря, либо строка,
 *     называющая нехватку и то, что её снимает.
 *   * СОСЕД ИЩЕТСЯ ПО ДАТЕ, А НЕ ПО ИНДЕКСУ (ниже).
 *   * СТОРОЖ ЗУМА НАЗЫВАЕТСЯ ВСЛУХ, когда слой включён, а тайлов никто не просит.
 *   * ШИРИНА НЕ РАСТЁТ: потолки 12 rem / 8.5 rem прежние, орган растёт вниз.
 *
 * Прозрачности у самого растра здесь по-прежнему нет: он рисуется как есть, а контуры полей
 * читаются потому, что слой вставлен ПОД ними (map/panes.ts).
 */

import { useEffect, useId } from "react";

import { T } from "../strings";
import { isMapTarget, isTypingTarget } from "../lib/dom";
import type { CalendarDay, CalendarModel } from "../lib/calendar";
import { formatDate, formatFull } from "../lib/format";
import type { SkipReason, TileBox } from "../lib/viewport";
import { Glyph, glyph, glyphSolid } from "./DrawTools";
import { SatelliteCalendar } from "./SatelliteCalendar";
import "./PreviewControl.css";

export type PreviewStatus = "idle" | "loading" | "ready" | "error";

/** Что видно на поверхности разработчика и нигде больше. */
export interface PreviewDevInfo {
  datesBbox: string | null;
  tileBox: TileBox | null;
  tileUrl: string | null;
  cacheHits: number;
  layers: number;
}

interface Props {
  calendar: CalendarModel;
  selectedDay: CalendarDay | null;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  status: PreviewStatus;
  error: string | null;
  acceptedOnly: boolean;
  onAcceptedOnly: (value: boolean) => void;
  calendarOpen: boolean;
  onCalendarToggle: (open: boolean) => void;
  datesLoading: boolean;
  datesError: string | null;
  /** Which cost guard emptied the list, so the panel says why instead of looking broken. */
  datesSkipped: SkipReason;
  /** Which cost guard is keeping the picture off the map while the switch says it is on. */
  tilesSkipped?: SkipReason;
  /** Спрашивали ли даты вообще (usePreview). Пустой список до вопроса — не «съёмок нет». */
  datesActive?: boolean;
  /** Служебный режим включён — тогда под календарём печатается разрез по слою. */
  dev?: PreviewDevInfo | null;
}

/**
 * «28 июля» → «28.07»: ниже 1100 px обе панели упираются в свои clamp-минимумы, и на дату
 * остаётся 2.75 rem. Словесный месяц туда не влезает — «28 июл» это шесть знаков моноширинного
 * 12.5 px = 45 px против 44 px бюджета, — поэтому под этим порогом дата сжимается в числа.
 * Полная запись выше порога словесная (`formatFull`), и второго словаря дат это не заводит:
 * в один момент времени на экране видна ровно одна из двух.
 */
const shortDate = (date: string | null): string => formatDate(date).slice(0, 5);

export function PreviewControl({
  calendar,
  selectedDay,
  selectedDate,
  onSelectDate,
  enabled,
  onToggle,
  status,
  error,
  acceptedOnly,
  onAcceptedOnly,
  calendarOpen,
  onCalendarToggle,
  datesLoading,
  datesError,
  datesSkipped,
  tilesSkipped = null,
  datesActive = false,
  dev = null,
}: Props) {
  const whyId = useId();

  // The filter narrows the ARROWS, never the calendar: the calendar's job is to show what
  // was skipped, and a filter that hid it would delete the answer.
  const visible = acceptedOnly
    ? calendar.days.filter((day) => day.mark === "accepted")
    : calendar.days;

  // СОСЕД ИЩЕТСЯ ПО ДАТЕ, А НЕ ПО ИНДЕКСУ — и это починка «жму и жму, ничего».
  // Прежний шаг искал `indexOf(selectedDate)` в отфильтрованном списке, а `disabled` стоял на
  // `index <= 0` и `index === -1`. Стоило выбрать в календаре отклонённую дату при включённом
  // фильтре «только чистые» — а он включается САМ, как только у поля появился прогон, — и
  // выбранной даты в списке не было: обе стрелки гасли молча. Сосед по дате определён всегда.
  const prevDay =
    selectedDate === null
      ? (visible.at(-1) ?? null)
      : ([...visible].reverse().find((day) => day.date < selectedDate) ?? null);
  const nextDay =
    selectedDate === null
      ? (visible[0] ?? null)
      : (visible.find((day) => day.date > selectedDate) ?? null);

  const step = (day: CalendarDay | null): void => {
    if (day) onSelectDate(day.date);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        // Раскрытие обязано закрываться с клавиатуры: клик по дате — не единственный путь.
        // В текстовом поле Escape принадлежит полю (там он снимает метку поиска).
        if (calendarOpen && !isTypingTarget(event.target)) {
          event.preventDefault();
          onCalendarToggle(false);
        }
        return;
      }
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      // Never steal arrows from a text field — or from the map surface, which pans with them.
      // Shared with the other two global handlers (lib/dom.ts): the rule was written out by
      // hand here and in map/shortcuts.ts, and drifted between them. `isMapTarget` excludes
      // this control's own buttons, which now sit inside the map container — that exclusion is
      // what makes ←/→ work while the control itself has focus.
      if (isTypingTarget(event.target) || isMapTarget(event.target)) return;
      event.preventDefault();
      step(event.key === "ArrowLeft" ? prevDay : nextDay);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // The handler closes over the current neighbours; re-binding on change keeps it correct.
  });

  // «i» — ровно у отклонённой даты. У принятой и неоценённой объяснять нечего: обе значат
  // «смотри снимок», а чем они отличаются между собой, знает пайплайн, а не копатель.
  const rejectedWhy =
    selectedDay?.mark === "rejected" && selectedDay.reason
      ? T.calendar.whyRejected(selectedDay.reason)
      : null;
  // Дата есть — значит есть чем управлять. Прочерк на её месте не печатается никогда: пустое
  // состояние ниже говорит либо что нажать, либо чего не хватает.
  const hasDate = calendar.total > 0 && selectedDate !== null;

  // Чего не хватает, когда даты нет. `null` — «ещё не спрашивали»: тогда это не нехватка, а
  // непочатое действие, и на месте строки стоит иконка календаря.
  const missing = datesLoading
    ? T.imagery.loadingDates
    : datesSkipped === "zoom"
      ? T.imagery.zoomHint
      : datesActive
        ? T.imagery.noDates
        : null;
  // Кнопки у строки нехватки нет намеренно: нажатие ничего не изменит, а приблизить карту —
  // изменит. Календарь без дат показал бы ровно ту же фразу второй раз.
  const showCalendarButton = hasDate || missing === null;
  // Тот же сторож не называется дважды: строка нехватки выше уже сказала это слово в слово.
  const showZoomNote = enabled && tilesSkipped === "zoom" && missing !== T.imagery.zoomHint;

  return (
    <div
      className={`k-datepill ${enabled ? "k-datepill--on" : ""}`}
      data-testid="preview-control"
      role="group"
      aria-label={T.imagery.title}
    >
      {hasDate && (
        <div className="k-datepill__bar">
          <button
            type="button"
            className="k-datepill__arrow"
            onClick={() => step(prevDay)}
            aria-label={T.preview.prev}
            title={T.preview.prev}
            disabled={prevDay === null}
          >
            ‹
          </button>

          {/* Дата — ПОКАЗАНИЕ, а не орган: она набрана моноширинным, как все измеренные
              величины продукта. Раскрытие календаря переехало на собственную иконку, и
              прежний спор «читается числом, работает кнопкой» этим и снят. */}
          <span className="k-datepill__date k-mono" data-testid="preview-date">
            <span className="k-datepill__full">{formatFull(selectedDate)}</span>
            <span className="k-datepill__short" aria-hidden="true">
              {shortDate(selectedDate)}
            </span>
          </span>

          <button
            type="button"
            className="k-datepill__arrow"
            onClick={() => step(nextDay)}
            aria-label={T.preview.next}
            title={T.preview.next}
            disabled={nextDay === null}
          >
            ›
          </button>
        </div>
      )}

      {!hasDate && missing !== null && (
        // Ограничитель назван, и названо действие, которое его снимает.
        <p className="k-caption k-datepill__empty">{missing}</p>
      )}

      {/* РЯД ИКОНОК. Тот же штрих, что у инструментов контура в соседнем углу; глаголы — в
          `aria-label` и `title`, как у них же. Переключатель рисуется и БЕЗ дат, пока слой
          включён: уехать на обзорный зум значит потерять список дат над областью, и без этой
          ветки включённый слой остался бы без выключателя. */}
      {(showCalendarButton || enabled) && (
        <div className="k-datepill__tools">
          {showCalendarButton && (
            <button
              type="button"
              className="k-datepill__tool"
              aria-expanded={calendarOpen}
              aria-label={calendarOpen ? T.calendar.close : T.calendar.open}
              title={calendarOpen ? T.calendar.close : T.calendar.open}
              onClick={() => onCalendarToggle(!calendarOpen)}
              data-testid="preview-calendar-btn"
            >
              <Glyph d={glyph.calendar} />
            </button>
          )}

          {(hasDate || enabled) && (
            // ГЛАЗ, А НЕ СТОПКА СЛОЁВ. Слой здесь ровно один, и вопрос не «какой из», а
            // «смотрю я на него или нет»; стопка же обещает выбор, которого нет. Плюс
            // словарь «снимок/карта» в этом же углу уже занят переключателем подложки
            // (BasemapSwitch) — вторая рамка с горизонтом читалась бы второй подложкой.
            // Нажатое состояние не цветом: зрачок ЗАЛИТ (полый у выключенного), к нему
            // рамка кнопки и тон тяжёлой линейки слева у всей панели.
            <button
              type="button"
              className={`k-datepill__tool ${
                status === "loading" && enabled ? "k-datepill__tool--busy" : ""
              }`}
              aria-pressed={enabled}
              aria-label={enabled ? T.imagery.hide : T.imagery.show}
              title={
                status === "loading" && enabled
                  ? T.preview.loading
                  : enabled
                    ? T.imagery.hide
                    : T.imagery.show
              }
              onClick={() => onToggle(!enabled)}
              data-testid="preview-toggle"
            >
              <Glyph d={glyph.eye} solid={enabled ? glyphSolid.pupil : undefined} />
            </button>
          )}

          {rejectedWhy && (
            <span className="k-datepill__why">
              {/* Кнопка, а не `title`: подсказка обязана открываться с клавиатуры и
                  переноситься по строкам. Поповер — сестра календаря, а не его потомок,
                  поэтому прокрутка календаря его не режет. */}
              <button
                type="button"
                className="k-datepill__whybtn"
                aria-label={T.calendar.why}
                aria-describedby={whyId}
              >
                i
              </button>
              <span className="k-datepill__whypop k-caption" id={whyId} data-testid="preview-mark">
                {rejectedWhy}
              </span>
            </span>
          )}
        </div>
      )}

      {calendarOpen && (
        <div className="k-datepill__pop" data-testid="preview-calendar-pop">
          <SatelliteCalendar
            model={calendar}
            selectedDate={selectedDate}
            onSelectDate={onSelectDate}
            acceptedOnly={acceptedOnly}
            onAcceptedOnly={onAcceptedOnly}
          />
          {dev && (
            <dl className="k-datepill__dev k-readout" data-testid="preview-dev">
              <dt>{T.dev.imagery.title}</dt>
              <dd>
                {T.dev.imagery.position(
                  selectedDate === null
                    ? 0
                    : visible.findIndex((day) => day.date === selectedDate) + 1,
                  visible.length,
                )}
              </dd>
              {/* Два бокса ОТДЕЛЬНЫМИ строками: их совпадение и было ошибкой. */}
              <dt>{T.dev.imagery.boxDates}</dt>
              <dd>{dev.datesBbox ?? T.common.noData}</dd>
              <dt>{T.dev.imagery.boxTiles}</dt>
              <dd>
                {dev.tileBox?.param ?? T.common.noData}
                {dev.tileBox &&
                  ` — ${
                    dev.tileBox.fallback
                      ? T.dev.imagery.rawBox
                      : T.dev.imagery.grid(dev.tileBox.cells, dev.tileBox.areaDeg2.toFixed(4))
                  }`}
              </dd>
              <dt>{T.dev.imagery.markCode}</dt>
              <dd>{selectedDay?.mark ?? T.common.noData}</dd>
              <dt>{T.dev.imagery.template}</dt>
              <dd className="k-datepill__devurl">{dev.tileUrl ?? T.common.noData}</dd>
              <dt>{T.dev.imagery.cacheHits(dev.cacheHits)}</dt>
              <dd>{T.dev.imagery.layers(dev.layers)}</dd>
              {/* Сырьё ошибки тайлов — здесь, не на продуктовой поверхности (preview-failed). */}
              <dt>{T.dev.imagery.lastError}</dt>
              <dd>{error ?? T.common.noData}</dd>
            </dl>
          )}
        </div>
      )}

      {/* СЛОЙ ВКЛЮЧЁН, А КАРТИНКИ НЕТ — и раньше об этом не говорил никто. Ниже зума 14 без
          выбранного поля бокс тайлов не строится вовсе (это самый дешёвый из сторожей), запрос
          не уходит, статус остаётся `idle`, а переключатель продолжал утверждать, что снимок
          показан. Сторож, которого не видно, неотличим от поломки. Своя формула (preview-zoom):
          примечание говорит, что снимка на этом масштабе НЕТ, — строка нехватки дат выше зовёт
          приблизить карту ради ПОИСКА. */}
      {showZoomNote && <div className="k-note k-note--warn">{T.preview.zoomNote}</div>}

      {datesError && <div className="k-note k-note--error">{T.imagery.datesFailed}</div>}

      {/* Только слово словаря (preview-failed): английский хвост «Failed to fetch» с экрана
          убран, сырой текст читается в служебном блоке календаря (dev.imagery.lastError). */}
      {status === "error" && <div className="k-note k-note--error">{T.preview.failed}</div>}
    </div>
  );
}
